/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { createEventSchema, DEFAULT_VALUES } from './schema';
import type { EventFormData } from './schema';
import { buildRecurrence, toIsoUtc } from '../event-form-utils';
import type { CalendarEventInput } from '@services/requests/housien-models/CalendarEventInput';

export interface UseEventDrawerFormProps {
  agencyId: string;
  calendars: Array<{ calendar_id: string; name: string; color?: string }>;
  editingEvent?: any;
  initialDate?: string;
  initialTime?: string;
  onSubmitCreate: (body: CalendarEventInput) => Promise<void>;
  onSubmitUpdate: (eventId: string, body: CalendarEventInput) => Promise<void>;
  onSuccess?: () => void;
}

const FREQ_KEYWORDS: Array<[string, EventFormData['frequency']]> = [
  ['DAILY', 'daily'],
  ['WEEKLY', 'weekly'],
  ['MONTHLY', 'monthly'],
];

/**
 * Derives the recurrence frequency from an iCal RRULE recurrence array.
 * @param recurrence - The event's recurrence rules, if any.
 * @returns The matching frequency, or 'none' when not recurring.
 */
function parseFrequency(recurrence?: string[]): EventFormData['frequency'] {
  const rule = recurrence?.[0];
  if (!rule) return 'none';
  const match = FREQ_KEYWORDS.find(([keyword]) => rule.includes(keyword));
  return match ? match[1] : 'none';
}

/**
 * Builds form defaults from an existing event being edited.
 * @param editingEvent - The event under edit.
 * @param calendars - Available calendars for fallback selection.
 * @returns Form values pre-populated from the event.
 */
function buildEditDefaults(
  editingEvent: any,
  calendars: UseEventDrawerFormProps['calendars']
): EventFormData {
  const start = editingEvent.start_at_utc ? dayjs(editingEvent.start_at_utc) : null;
  const end = editingEvent.end_at_utc ? dayjs(editingEvent.end_at_utc) : null;
  const fmt = (d: dayjs.Dayjs | null, pattern: string) => (d ? d.format(pattern) : '');
  return {
    title: editingEvent.title ?? '',
    calendarId: editingEvent.calendar_id ?? calendars[0]?.calendar_id ?? '',
    date: fmt(start, 'YYYY-MM-DD'),
    startTime: fmt(start, 'HH:mm'),
    endTime: fmt(end, 'HH:mm'),
    allDay: editingEvent.is_all_day ?? false,
    frequency: parseFrequency(editingEvent.recurrence),
    description: editingEvent.description ?? '',
  };
}

/**
 * Builds form defaults for creating a new event.
 * @param props - The drawer props including calendars and initial date/time.
 * @returns Form values seeded with sensible create-mode defaults.
 */
function buildCreateDefaults(props: UseEventDrawerFormProps): EventFormData {
  const { calendars, initialDate, initialTime } = props;
  const date = initialDate || dayjs().format('YYYY-MM-DD');
  const raw = initialTime ? dayjs(`${date}T${initialTime}`) : dayjs();
  // Round up to the next half hour so opening the drawer at 15:37 proposes
  // 16:00 rather than a ragged 15:37–16:37 slot. A clicked grid slot is
  // already on the hour, so it survives the rounding unchanged.
  const base = raw.second(0).millisecond(0).add(
    (30 - (raw.minute() % 30)) % 30,
    'minute'
  );
  return {
    ...DEFAULT_VALUES,
    calendarId: calendars[0]?.calendar_id || '',
    date,
    startTime: base.format('HH:mm'),
    endTime: base.add(1, 'hour').format('HH:mm'),
  };
}

/**
 * Selects create or edit defaults depending on the drawer mode.
 * @param props - The drawer props.
 * @returns The initial form values for the current mode.
 */
function buildDefaultValues(props: UseEventDrawerFormProps): EventFormData {
  if (props.editingEvent) {
    return buildEditDefaults(props.editingEvent, props.calendars);
  }
  return buildCreateDefaults(props);
}

/**
 * Resolves the UTC start/end instants for a submitted event, using the whole
 * day's bounds when `allDay` is set.
 *
 * @param data - The submitted form data.
 * @returns The start/end ISO UTC instants.
 */
function resolveEventBounds(data: EventFormData): { startUtc: string; endUtc: string } {
  if (data.allDay) {
    return {
      startUtc: dayjs(`${data.date}T00:00`).toISOString(),
      endUtc: dayjs(`${data.date}T23:59`).toISOString(),
    };
  }
  return {
    startUtc: toIsoUtc(data.date, data.startTime),
    endUtc: toIsoUtc(data.date, data.endTime),
  };
}

/**
 * Builds the API payload for a submitted event.
 *
 * @param data - The submitted form data.
 * @returns The calendar event input payload.
 */
function buildEventPayload(data: EventFormData): CalendarEventInput {
  const { startUtc, endUtc } = resolveEventBounds(data);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return {
    calendar_id: data.calendarId,
    title: data.title,
    description: data.description || null,
    start_at_utc: startUtc,
    start_timezone: timezone,
    end_at_utc: endUtc,
    end_timezone: timezone,
    is_all_day: data.allDay,
    recurrence: buildRecurrence(data.frequency),
  };
}

/**
 * Manages the event drawer form: schema, defaults, submission and validation state.
 * @param props - Agency context, calendars, editing event, and submit callbacks.
 * @returns Form instance plus submit handler and status flags.
 */
export function useEventDrawerForm(props: UseEventDrawerFormProps) {
  const { editingEvent, onSubmitCreate, onSubmitUpdate, onSuccess } = props;
  const { t } = useTranslation('features/agenda');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);

  const schema = useMemo(() => createEventSchema(t), [t]);

  const defaultValues = useMemo(() => buildDefaultValues(props), [
    editingEvent?.event_id, props.agencyId, props.initialDate, props.initialTime,
  ]);

  const form = useForm<EventFormData>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onSubmit',
  });

  const onValid = useCallback(async (data: EventFormData) => {
    setIsSubmitting(true);
    setHasValidationErrors(false);
    try {
      const payload = buildEventPayload(data);

      if (editingEvent?.event_id) {
        await onSubmitUpdate(editingEvent.event_id, payload);
      } else {
        await onSubmitCreate(payload);
      }
      onSuccess?.();
    } finally {
      setIsSubmitting(false);
    }
  }, [editingEvent, onSubmitCreate, onSubmitUpdate, onSuccess]);

  const onInvalid = useCallback(() => {
    setHasValidationErrors(true);
  }, []);

  const handleSubmit = useCallback(
    () => form.handleSubmit(onValid, onInvalid)(),
    [form, onValid, onInvalid],
  );

  return {
    form,
    isSubmitting,
    hasValidationErrors,
    handleSubmit,
    isEditMode: !!editingEvent,
  };
}
